import { FacebookMetric, GoogleMetric, Region, SitewideMetric } from "services/CompareService";

export interface SelectOption {
  value: string;
  label: string;
}

export const FACEBOOK_METRIC_LABELS: Record<FacebookMetric, string> = {
  spend: "Ad Spend ($)",
  roas: "ROAS",
  cpa: "Cost Per Acquisition ($)",
  aov: "Average Order Value ($)",
  cpc: "Cost Per Click ($)",
  cpm: "Cost Per Mille ($)",
  ctr: "Click Through Rate (%)",
  cvr: "Conversion Rate (%)",
  impressions: "Impressions",
  clicks: "Clicks",
  purchases: "Purchases",
  purchase_conversion_value: "Purchase Value ($)"
};

export const FACEBOOK_METRIC_FORMATTERS: Record<FacebookMetric, (value: number) => string> = {
  spend: (value) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  roas: (value) => value.toFixed(2),
  cpa: (value) => `$${value.toFixed(2)}`,
  aov: (value) => `$${value.toFixed(2)}`,
  cpc: (value) => `$${value.toFixed(4)}`,
  cpm: (value) => `$${value.toFixed(4)}`,
  ctr: (value) => `${value.toFixed(2)}%`,
  cvr: (value) => `${value.toFixed(2)}%`,
  impressions: (value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 }),
  clicks: (value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 }),
  purchases: (value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 }),
  purchase_conversion_value: (value) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
};

export const GOOGLE_METRIC_LABELS: Record<GoogleMetric, string> = {
  spend: "Ad Spend ($)",
  purchase_conversion_value: "Purchase Value ($)",
  roas: "ROAS",
  cpa: "Cost Per Acquisition ($)",
  aov: "Average Order Value ($)",
  cpc: "Cost Per Click ($)",
  ctr: "Click Through Rate (%)",
  cvr: "Conversion Rate (%)",
  impressions: "Impressions",
  clicks: "Clicks",
  conversions: "Conversions"
};

export const GOOGLE_METRIC_FORMATTERS: Record<GoogleMetric, (value: number) => string> = {
  spend: (value) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  purchase_conversion_value: (value) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  roas: (value) => value.toFixed(2),
  cpa: (value) => `$${value.toFixed(2)}`,
  aov: (value) => `$${value.toFixed(2)}`,
  cpc: (value) => `$${value.toFixed(4)}`,
  ctr: (value) => `${value.toFixed(2)}%`,
  cvr: (value) => `${value.toFixed(2)}%`,
  impressions: (value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 }),
  clicks: (value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 }),
  conversions: (value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 })
};

export const SITEWIDE_METRIC_LABELS: Record<SitewideMetric, string> = {
  spend: "Total Spend (sum)",
  mer: "MER (avg)",
  amer: "AMER (avg)",
  cac: "CAC (avg)",
  aov: "AOV (avg)"
};

export const SITEWIDE_METRIC_FORMATTERS: Record<SitewideMetric, (value: number) => string> = {
  spend: (value) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  mer: (value) => value.toFixed(2),
  amer: (value) => value.toFixed(2),
  cac: (value) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  aov: (value) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
};

export const REGIONS: Region[] = [
  "North America",
  "Europe",
  "UK & Ireland",
  "Australia & NZ",
  "Asia Pacific",
  "Others"
];

export const ALL_REGIONS_OPTION: SelectOption = { value: "", label: "All Regions" };

export const getRegionOptions = (): SelectOption[] => {
  return [
    ALL_REGIONS_OPTION,
    ...REGIONS.map((region) => ({
      value: region,
      label: region,
    }))
  ];
};

export const getMetricOptions = <T extends string>(metricLabels: Record<T, string>): SelectOption[] => {
  return Object.entries(metricLabels).map(entry => {
    const [value, label] = entry;
    return {
      value,
      label: label as string
    };
  });
};
